import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MaterialTable, { MTableToolbar } from "material-table";
import moment from "moment";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import Select from "react-select";
import Input from "@material-ui/core/Input";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Search from "@material-ui/icons/Search";

import FormEdit from "./FormEdit";
import Detail from "./Detail";
import useDebounce from "../global/use-debounce";
import { getMalls } from "../../actions/admin/mall";
import { getCategories } from "../../actions/admin/category";
import { getStores } from "../../actions/admin/store";
import { getPaginatePromos, deletePromo } from "../../actions/admin/promo";
import { updateModalContent, toggleModal } from "../../actions/modal";

const List = ({
    getCategories,
    getStores,
    getMalls,
    deletePromo,
    updateModalContent,
    toggleModal,
    getPaginatePromos,
    promo: { updatedAt }
}) => {
    const formEditRef = useRef();

    const table = useRef();

    const [pageSize, setPageSize] = useState(5);

    const initializeFilterValue = {
        category: null,
        store: null,
        mall: null
    };

    const initializeFocusValue = {
        searchFocus: false
    };

    const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [mallOptions, setMallOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [filterData, setFilterData] = useState(initializeFilterValue);

    const { category, store, mall } = filterData;

    const [search, setSearch] = useState("");

    const debouncedSearch = useDebounce(search, 500);

    const { searchFocus } = autoFocus;

    const [onReset, setOnReset] = useState(false);

    const columns = [
        { title: "ID", field: "id", defaultSort: "asc" },
        {
            title: "Title",
            field: "title",
            render: promo => {
                return (
                    <span
                        onClick={() => onEditPromo(promo)}
                        style={{ cursor: "pointer" }}
                        className="quick-edit"
                    >
                        {promo.title}
                    </span>
                );
            }
        },
        {
            title: "Description",
            field: "description",
            cellStyle: {
                width: "40%"
            }
        },
        { title: "Source", field: "source" },
        {
            title: "Date From",
            field: "dateFrom",
            render: rowData => moment(rowData.dateFrom).format("DD-MM-YYYY HH:mm")
        },
        {
            title: "Date Till",
            field: "dateTill",
            render: rowData => {
                return moment(rowData.dateTill).format("DD-MM-YYYY HH:mm");
            }
        }
    ];

    useEffect(() => {
        table.current && table.current.onQueryChange();
    }, [filterData, updatedAt]);

    useEffect(() => {
        if (!onReset) {
            table.current && table.current.onQueryChange();
        } else {
            setOnReset(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    useEffect(() => {
        const fetchCategoriesData = async () => {
            const categories = await getCategories();
            const categoryOptions = categories.map(category => {
                return {
                    value: category.id,
                    label: category.name
                };
            });
            setCategoryOptions(categoryOptions);
        };

        fetchCategoriesData();
    }, [getCategories]);

    useEffect(() => {
        const fetchMallsData = async () => {
            const malls = await getMalls();
            if (malls && malls.length) {
                const mallOptions = malls.map(mall => {
                    return {
                        value: mall.id,
                        label: mall.name
                    };
                });
                setMallOptions(mallOptions);
            }
        };

        fetchMallsData();
    }, [getMalls]);

    useEffect(() => {
        const fetchStoresData = async () => {
            const stores = await getStores({});
            if (stores && stores.length) {
                const storeOptions = stores.map(store => {
                    return {
                        value: store.id,
                        label: store.name
                    };
                });
                setStoreOptions(storeOptions);
            }
        };

        fetchStoresData();
    }, [getStores]);

    const handleCategoryChange = selectedOption => {
        setFilterData({ ...filterData, category: selectedOption });

        setAutoFocus({ ...autoFocus, searchFocus: false });
    };

    const handleMallChange = selectedOption => {
        setFilterData({ ...filterData, mall: selectedOption });

        setAutoFocus({ ...autoFocus, searchFocus: false });
    };

    const handleStoreChange = selectedOption => {
        setFilterData({ ...filterData, store: selectedOption });

        setAutoFocus({ ...autoFocus, searchFocus: false });
    };

    const handleSearchChange = e => {
        setSearch(e.target.value);

        setAutoFocus({ ...autoFocus, searchFocus: true });
    };

    const onResetFilter = () => {
        setFilterData(initializeFilterValue);
        setOnReset(true);
        setSearch("");

        setAutoFocus({ ...autoFocus, searchFocus: false });
    };

    const onDeletePromo = id => {
        updateModalContent({
            title: "Are you sure?",
            content: "All data related to this promo will be deleted!",
            actionButton: {
                actionText: "Yes, Delete",
                actionType: "danger",
                action: () => deletePromo(id)
            },
            cancelText: "No",
            closeAfterAction: true
        });

        toggleModal();
    };

    const onEditPromo = promo => {
        updateModalContent({
            title: "Update Promo",
            content: <FormEdit id={promo.id} ref={formEditRef} />,
            actionButton: {
                actionText: "Save",
                actionType: "primary",
                action: () => formEditRef.current.submitForm()
            },
            cancelText: "Cancel",
            closeAfterAction: false,
            className: "wide"
        });

        toggleModal();
    };

    const onViewPromo = promo => {
        updateModalContent({
            title: "Promo Detail",
            content: <Detail id={promo.id} />,
            cancelText: "Close",
            closeAfterAction: true,
            className: "wide"
        });

        toggleModal();
    };

    return (
        <MaterialTable
            tableRef={table}
            className={"material-table"}
            title="Promo List"
            columns={columns}
            onChangeRowsPerPage={pageSize => {
                setAutoFocus(initializeFocusValue);
                setPageSize(pageSize);
            }}
            data={query =>
                new Promise(async (resolve, reject) => {
                    try {
                        query.category = category ? category.value : null;
                        query.mallName = mall ? mall.label : null;
                        query.storeName = store ? store.label : null;
                        query.search = search;

                        const result = await getPaginatePromos(query);

                        let { promos, totalPromos } = result;
                        let page = query.page;

                        if (promos.length === 0) {
                            page = 0;

                            if (totalPromos !== 0) {
                                const resultFirstPage = await getPaginatePromos(
                                    { ...query, page }
                                );

                                promos = resultFirstPage.promos;
                            }
                        }

                        resolve({
                            data: promos,
                            page: page,
                            totalCount: totalPromos
                        });
                    } catch (err) {
                        resolve({
                            data: [],
                            page: 0,
                            totalCount: 0
                        });
                    }
                })
            }
            actions={[
                {
                    icon: "visibility",
                    tooltip: "View Promo",
                    onClick: (event, promo) => onViewPromo(promo)
                },
                {
                    icon: "edit",
                    tooltip: "Edit Promo",
                    onClick: (event, promo) => onEditPromo(promo)
                },
                {
                    icon: "delete",
                    tooltip: "Delete Promo",
                    onClick: (event, promo) => onDeletePromo(promo.id)
                }
            ]}
            options={{
                actionsColumnIndex: -1,
                pageSize,
                emptyRowsWhenPaging: false,
                search: false
            }}
            components={{
                Toolbar: props => (
                    <div>
                        <MTableToolbar {...props} />
                        <Grid
                            container
                            alignItems="center"
                            className="material-filters"
                        >
                            <Grid
                                container
                                item
                                xs={12}
                                md={9}
                                alignItems="center"
                            >
                                <Grid item xs={12} sm>
                                    <FormControl
                                        fullWidth
                                        style={{
                                            zIndex: 14,
                                            padding: 8
                                        }}
                                    >
                                        <Select
                                            value={category}
                                            onChange={handleCategoryChange}
                                            options={categoryOptions}
                                            closeMenuOnSelect={false}
                                            blurInputOnSelect={false}
                                            placeholder={"Category"}
                                            isClearable
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <FormControl
                                        fullWidth
                                        style={{
                                            zIndex: 13,
                                            padding: 8
                                        }}
                                    >
                                        <Select
                                            value={mall}
                                            onChange={handleMallChange}
                                            options={mallOptions}
                                            closeMenuOnSelect={false}
                                            blurInputOnSelect={false}
                                            placeholder={"Mall"}
                                            isClearable
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm>
                                    <FormControl
                                        fullWidth
                                        style={{
                                            zIndex: 12,
                                            padding: 8
                                        }}
                                    >
                                        <Select
                                            value={store}
                                            onChange={handleStoreChange}
                                            options={storeOptions}
                                            closeMenuOnSelect={false}
                                            blurInputOnSelect={false}
                                            placeholder={"Store"}
                                            isClearable
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    sm
                                    className="reset-button-container"
                                >
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={onResetFilter}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                item
                                justify="center"
                                alignItems="center"
                                xs={12}
                                md={3}
                            >
                                <Grid item xs={6} md={9}>
                                    <FormControl fullWidth>
                                        <Input
                                            id="standard-adornment-amount"
                                            placeholder={"Search"}
                                            value={search}
                                            onChange={handleSearchChange}
                                            type="search"
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            }
                                            autoFocus={searchFocus}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </div>
                )
            }}
        />
    );
};

List.postTypes = {
    promo: PropTypes.object.isRequired,
    getPaginatePromos: PropTypes.func.isRequired,
    getCategories: PropTypes.func.isRequired,
    getStores: PropTypes.func.isRequired,
    getMalls: PropTypes.func.isRequired,
    updateModalContent: PropTypes.func.isRequired,
    toggleModal: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    promo: state.admin.promo
});

export default connect(mapStateToProps, {
    getCategories,
    getStores,
    getMalls,
    toggleModal,
    updateModalContent,
    deletePromo,
    getPaginatePromos
})(List);
